import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link2, Unlink, Pencil, Plus, Rows3, Trash2, GitFork } from "lucide-react";
import { StatusPill, inServerText, RelocateHint } from "@/components/network/StatusPill";
import { ArahkanButton } from "@/components/network/ArahkanButton";
import { PortFormDialog, BulkPortDialog, ConnectDialog, DisconnectDialog, MigrateDialog } from "@/components/network/OltDialogs";
import { OltTopology } from "@/components/network/OltTopology";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { KodeHistory } from "@/components/network/KodeHistory";
import { ServerInfo } from "@/components/network/ServerInfo";
import { apiError } from "@/lib/network";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

function Info({ k, v }) {
  return <div className="grid grid-cols-[120px_1fr] gap-2 border-b border-[#F3F4F6] py-1 text-xs"><span className="text-[#6B7280]">{k}</span><span>{v || "-"}</span></div>;
}

function PortRow({ p, olt, canManage, act }) {
  return (
    <tr className="border-t" data-testid={`port-row-${p.nama_port}`}>
      <td className="p-2 font-mono-tab font-semibold">{p.nama_port}</td>
      <td>{p.kapasitas_onu ?? "-"}</td>
      <td><StatusPill status={p.status} /></td>
      <td>{p.link ? <span data-testid={`port-odc-${p.nama_port}`}>{p.link.odc?.kode}</span>
        : p.splitter_link ? <span data-testid={`port-splitter-${p.nama_port}`}>Splitter {p.splitter_link.splitter?.kode} ({p.splitter_link.splitter?.rasio})</span>
        : <span className="text-[#6B7280]">Kosong</span>}</td>
      <td className="whitespace-nowrap pr-2 text-right">
        {p.splitter_link && <span className="text-[11px] text-[#6B7280]">Kelola di <span className="hidden sm:inline">Master </span>Splitter</span>}
        {canManage && !p.splitter_link && (p.link
          ? <><Button size="sm" variant="ghost" disabled={p.status === "NONAKTIF" || olt.status === "NONAKTIF"} onClick={() => act("migrate", p)} data-testid={`port-migrate-${p.nama_port}`} title="Pindahkan ke Splitter"><GitFork className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Pindahkan ke Splitter</span></Button>
            <Button size="sm" variant="ghost" onClick={() => act("disconnect", p)} data-testid={`port-disconnect-${p.nama_port}`}><Unlink className="mr-1 h-3.5 w-3.5" />Putus</Button></>
          : <Button size="sm" variant="ghost" disabled={p.status !== "AKTIF" || olt.status !== "AKTIF"} onClick={() => act("connect", p)} data-testid={`port-connect-${p.nama_port}`}>
            <Link2 className="mr-1 h-3.5 w-3.5" />Hubungkan</Button>)}
        {canManage && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => act("edit", p)} data-testid={`port-edit-${p.nama_port}`}><Pencil className="h-3.5 w-3.5" /></Button>}
        {canManage && !p.has_history && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => act("delete", p)} data-testid={`port-delete-${p.nama_port}`}>
          <Trash2 className="h-3.5 w-3.5 text-[#DC2626]" /></Button>}
      </td>
    </tr>
  );
}

export function OltDetail({ oltId, canManage, me, wilayahName, lokasiName, onChanged, onDeleted, onSelect }) {
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState({});
  const [k, setK] = useState(0);
  const load = useCallback(() => api.get(`/network/olt/${oltId}`).then((r) => setD(r.data)).catch(() => toast.error("Gagal memuat OLT")), [oltId]);
  useEffect(() => { load(); }, [load, k]);
  const done = () => { setDlg({}); setK((x) => x + 1); onChanged?.(); };
  const act = (type, p) => setDlg({ [type]: p });
  const del = async () => {
    try { await api.delete(`/network/pon-ports/${dlg.delete.id}`); toast.success("Port dihapus"); done(); } catch (err) { toast.error(apiError(err)); setDlg({}); }
  };
  if (!d) return <p className="p-3 text-xs">Memuat…</p>;
  return (
    <div className="min-w-0 space-y-3" data-testid="olt-detail">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div><div className="font-display text-xl font-bold" data-testid="olt-detail-kode">{d.kode} <StatusPill status={d.status} /></div>
          <div className="text-xs text-[#6B7280]">{d.nama} · {d.vendor} {d.model} · {d.teknologi}</div></div>
        <div className="flex gap-2">
          {d.has_coords && !d.in_server && <ArahkanButton lat={d.latitude} lng={d.longitude} testid="olt-nav-btn" />}
          <DeleteInfraButton kind="olt" item={d} me={me} onDeleted={onDeleted} />
        </div>
      </div>
      <Tabs defaultValue="port">
        <TabsList><TabsTrigger value="port" data-testid="olt-tab-port">Port PON ({d.port_count})</TabsTrigger>
          <TabsTrigger value="topo" data-testid="olt-tab-topo">Topologi</TabsTrigger><TabsTrigger value="info" data-testid="olt-tab-info">Info</TabsTrigger></TabsList>
        <TabsContent value="port" className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span data-testid="olt-port-usage">{d.port_terpakai} terpakai · {d.port_count - d.port_terpakai} kosong</span>
            {canManage && d.status !== "NONAKTIF" && <div className="flex flex-wrap gap-1">
              <Button size="sm" variant="outline" onClick={() => setDlg({ bulk: true })} data-testid="olt-bulk-port-btn"><Rows3 className="mr-1 h-3.5 w-3.5" />Buat Sekaligus</Button>
              <Button size="sm" onClick={() => setDlg({ edit: {} })} data-testid="olt-add-port-btn"><Plus className="mr-1 h-3.5 w-3.5" />Tambah Port</Button></div>}
          </div>
          <div className="max-h-[50vh] max-w-full overflow-auto rounded border" data-testid="olt-port-table-wrap">
            <table className="w-full text-sm" data-testid="olt-port-table">
              <thead className="sticky top-0 bg-[#F8F9FA] text-left text-xs uppercase text-[#6B7280]"><tr><th className="p-2">Port</th><th>Maks ONU</th><th>Status</th><th>ODC</th><th /></tr></thead>
              <tbody>
                {d.ports.map((p) => <PortRow key={p.id} p={p} olt={d} canManage={canManage} act={act} />)}
                {!d.ports.length && <tr><td colSpan={5} className="p-4 text-center text-xs text-[#6B7280]" data-testid="olt-port-empty">
                  Belum ada port PON. {canManage ? "Tambahkan sesuai port yang terpasang di perangkat." : ""}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="topo"><OltTopology oltId={oltId} canManage={canManage} reloadKey={k} /></TabsContent>
        <TabsContent value="info">
          <Info k="Lokasi" v={lokasiName(d.lokasi_id)} /><Info k="Wilayah" v={wilayahName(d.wilayah_id)} /><Info k="Serial Number" v={d.serial_number} />
          <Info k="IP Manajemen" v={d.management_ip} /><Info k="Alamat" v={d.alamat} /><Info k="Koordinat" v={d.in_server ? inServerText(d.in_server) : d.has_coords ? `${d.latitude}, ${d.longitude}` : "Belum diisi"} />
          <Info k="Catatan" v={d.catatan} /><Info k="Diubah" v={`${d.updated_by} · ${(d.updated_at || "").slice(0, 10)}`} />
          <div className="mt-3"><RelocateHint show={!d.in_server && !d.has_coords && d.port_terpakai > 0} testid="olt-relocate-hint" /></div>
          <div className="mt-3"><ServerInfo kind="olt" id={d.id} onSelect={onSelect} /></div>
          <div className="mt-3"><KodeHistory kind="olt" id={d.id} kode={d.kode} /></div>
        </TabsContent>
      </Tabs>
      <PortFormDialog open={!!dlg.edit} olt={d} port={dlg.edit?.id ? dlg.edit : null} onClose={() => setDlg({})} onSaved={done} />
      <BulkPortDialog open={!!dlg.bulk} olt={d} onClose={() => setDlg({})} onSaved={done} />
      <ConnectDialog port={dlg.connect} olt={d} onClose={() => setDlg({})} onSaved={done} />
      <DisconnectDialog port={dlg.disconnect} onClose={() => setDlg({})} onSaved={done} />
      <MigrateDialog port={dlg.migrate} olt={d} onClose={() => setDlg({})} onSaved={done} />
      <AlertDialog open={!!dlg.delete} onOpenChange={(v) => !v && setDlg({})}>
        <AlertDialogContent data-testid="port-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Hapus port {dlg.delete?.nama_port}?</AlertDialogTitle>
            <AlertDialogDescription>Port ini belum pernah terhubung ke ODC, jadi aman dihapus. Port yang punya riwayat hanya bisa dinonaktifkan.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="port-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-[#DC2626]" data-testid="port-delete-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
